import React, { useState, useEffect, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import './EllipsisMenu.scss';
import Icon from '../IconSvg/Icon';
import { LelyIconButton } from '@/common/components';

export interface AvailableOptions {
  title: string;
  label?: string;
  onClickFn?: React.MouseEventHandler<HTMLLIElement>;
  icon?: string;
}
interface Props {
  availableOptions: AvailableOptions[];
  setSelectedItemElipsis?: (state: any) => void;
  selectedItemElipsis: any;
  tableRef?: any;
  items?: any;
  modalHeight?: number;
}

export default function EllipsisMenu({
  availableOptions,
  setSelectedItemElipsis,
  selectedItemElipsis,
  tableRef,
  items,
  modalHeight = 169,
}: Props) {
  const elipsisRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLUListElement>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [itemOffsetTop, setItemOffsetTop] = useState(0);
  const [tablePosition, setTablePosition] = useState({ top: 0, bottom: 0 });
  const [itemOffsetHeight, setItemOffsetHeight] = useState(0);
  const [scrolltop, setScrolltop] = useState(0);
  const [isTableInDOM, setIsTableInDOM] = useState<boolean | Date>(false); // Date is used in order to have a unique value/timestamp that can trigger useEffect execution (because otherwise true/true doesn't trigger that)
  const [top, setTop] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>(
    'bottom',
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (tableRef?.current && tableRef?.current.rows.length > 1) {
        setIsTableInDOM(new Date());
        clearInterval(interval);
      }
    }, 100);
    return () => clearInterval(interval);
  }, [items]); // items is used in order to refresh the tableRef logic/listeners once new data has arrived (e.g.: pressing the Apply button on a new date range has brought new data in the reducer)

  useEffect(() => {
    function handleClickOutside(event): void {
      if (
        ((tableRef && !event?.target?.closest('.ellipsis-menu-button')) ||
          !tableRef) &&
        !elipsisRef?.current?.contains(event.target)
      ) {
        setIsOpen(false);
        setTop(null);
        if (setSelectedItemElipsis) setSelectedItemElipsis(null);
      }
    }
    if (tableRef) {
      document.addEventListener('click', handleClickOutside);
    } else {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedItemElipsis === null) {
      setIsOpen(false);
      setTop(null);
    }
  }, [selectedItemElipsis]);

  useEffect(() => {
    if (!isTableInDOM) return;

    const onClickFunc = (e) => {
      if (e?.target?.closest('.ellipsis-menu-button')) {
        const getTablePosition = tableRef?.current.getBoundingClientRect();
        setTablePosition({
          top: getTablePosition.top,
          bottom: getTablePosition.bottom,
        });
        setItemOffsetTop(
          e?.target?.closest('.ellipsis-menu-button').parentElement
            .parentElement.offsetTop,
        );
        setItemOffsetHeight(
          e?.target?.closest('.ellipsis-menu-button').parentElement
            .parentElement.offsetHeight,
        );
        setScrolltop(tableRef?.current.scrollTop);
      }
    };

    const onScrollFunc = () => {
      setScrolltop(tableRef?.current.scrollTop);
    };

    tableRef?.current?.addEventListener('click', onClickFunc);
    tableRef?.current?.addEventListener('scroll', onScrollFunc);

    return () => {
      tableRef?.current?.removeEventListener('click', onClickFunc);
      tableRef?.current?.removeEventListener('scroll', onScrollFunc);
    };
  }, [isTableInDOM]); // new table ref/table HTML element has been drawn; set new click/scroll listeners to it

  useEffect(() => {
    if (!tableRef?.current || !selectedItemElipsis) return;
    let topElipsis = itemOffsetTop + itemOffsetHeight - scrolltop;

    if (topElipsis < tablePosition.top || topElipsis > tablePosition.bottom) {
      setIsOpen(false);
      setTop(null);
      if (setSelectedItemElipsis) setSelectedItemElipsis(null);
      return;
    }

    const windowHeight = window.innerHeight;
    const rowHeight = 40;
    const headerHeight = 59;

    if (modalHeight + topElipsis + headerHeight > windowHeight) {
      topElipsis -= modalHeight + rowHeight - 16;
    }
    setTop(`${topElipsis}px`);
    setIsOpen(true);
  }, [
    tablePosition,
    itemOffsetTop,
    itemOffsetHeight,
    scrolltop,
    tableRef?.current,
    selectedItemElipsis,
  ]);

  useEffect(() => {
    if (isOpen && buttonRef.current && dropdownRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      if (spaceBelow >= dropdownRect.height) {
        setDropdownPosition('bottom');
      } else if (spaceAbove >= dropdownRect.height) {
        setDropdownPosition('top');
      } else {
        // If there's not enough space above or below, default to bottom
        setDropdownPosition('bottom');
      }
    }
  }, [isOpen]);

  const handleDotsClick: React.MouseEventHandler<HTMLButtonElement> = (
    event,
  ) => {
    event.stopPropagation();
    setIsOpen(!isOpen);
  };
  const ellipsisDropdownClassName = `ellipsis-menu-dropdown ${
    isOpen ? 'is-visible' : ''
  } ${dropdownPosition}`;
  const stylesForEventsPage = {
    top: top || 'unset',
    right: '50px',
    marginTop: 0,
  };
  return (
    <div
      ref={elipsisRef}
      className="ellipsis-menu top-navigation__btns"
      style={top ? { position: 'static' } : undefined}
    >
      {!setSelectedItemElipsis && (
        <LelyIconButton
          ref={buttonRef}
          className="ellipsis-menu-button btn btn-square mr-2"
          type="button"
          id="dropdownOptionsButton"
          data-toggle="dropdown"
          aria-haspopup="true"
          aria-expanded="false"
          icon="lely-icon-more-vertical"
          size="md"
          onClick={handleDotsClick}
        />
      )}
      <ul
        ref={dropdownRef}
        className={ellipsisDropdownClassName}
        aria-labelledby="dropdownOptionsButton"
        style={top ? stylesForEventsPage : undefined}
      >
        {availableOptions.map((currentOption: AvailableOptions, i: any) => {
          if (!isEmpty(currentOption)) {
            if (currentOption.title === 'title') {
              return (
                <li
                  className="dropdown-item"
                  // eslint-disable-next-line react/no-array-index-key
                  key={currentOption.title + i}
                >
                  <p className="title fs--b-sm">{currentOption.label}</p>
                </li>
              );
            }
            if (currentOption.title === 'Underline') {
              return (
                <li
                  className="dropdown-item underline"
                  // eslint-disable-next-line react/no-array-index-key
                  key={currentOption.title + i}
                >
                  <u className="underline-tag" />
                </li>
              );
            }
            return (
              <li
                className="dropdown-item fs--b-base"
                // eslint-disable-next-line react/no-array-index-key
                key={currentOption.label + i}
                onClick={currentOption.onClickFn}
              >
                <a
                  className="btn btn-secondary"
                  style={top ? { border: 0, boxShadow: 'none' } : undefined}
                >
                  {currentOption.icon && (
                    <Icon
                      name={currentOption.icon}
                      style={{ width: 19, height: 19, marginRight: 5 }}
                    />
                  )}
                  {currentOption.label}
                </a>
              </li>
            );
          }
          return null;
        })}
      </ul>
    </div>
  );
}
